/**
 * Domain hooks for page/block-related PowerSync queries (Folio).
 *
 * Same contract as `use-lists-query.ts`: each hook embeds the canonical
 * queryKey + queryFn so consumers can't create mismatched subscriptions.
 */

import type { Accessor } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { Attachment, Block, Page, PageVersion, PowerSyncDrizzleDB } from "@folio/powersync";

import {
  type BacklinkRow,
  type BlockStatRow,
  getAllBlocksQuery,
  getAttachmentsByPageQuery,
  getBacklinksQuery,
  getDeckPagesQuery,
  getPageBlocksQuery,
  getPageQuery,
  getPagesQuery,
  getPageVersionsQuery,
  getTrashedPagesQuery,
  getWorkbookPagesQuery,
  queryKeys,
  searchPagesQuery,
} from "../index";

import { createPowerSyncQuery, createPowerSyncSingle } from "./create-power-sync-query";

interface BasePageQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  /** Stale time in ms. Pass your app's OFFLINE_CACHE_STALE_TIME_MS. */
  staleTime?: number;
  throttleTime?: number;
}

/**
 * All visible pages (own + shared), ordered by sort_key.
 * A single authoritative subscription: tree is built in memory.
 */
export function usePagesQuery(
  options: BasePageQueryOptions & { userId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<Page>({
    queryKey: () => queryKeys.pages.tree(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      if (!db) {
        return null;
      }
      return getPagesQuery(db);
    },
    enabled: () => Boolean(options.userId()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/**
 * Page detail by id.
 */
export function usePageDetailQuery(
  options: BasePageQueryOptions & { pageId: Accessor<string | undefined> },
) {
  return createPowerSyncSingle<Page>({
    queryKey: () => queryKeys.pages.detail(options.pageId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const pageId = options.pageId();
      if (!db || !pageId) {
        return null;
      }
      return getPageQuery(db, pageId);
    },
    enabled: () => Boolean(options.pageId()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/**
 * Page blocks in document order. Non-suspense path: the editor
 * manages its own loading state and applies remote changes manually.
 */
export function usePageBlocksQuery(
  options: BasePageQueryOptions & { pageId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<Block>({
    queryKey: () => queryKeys.pages.blocks(options.pageId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const pageId = options.pageId();
      if (!db || !pageId) {
        return null;
      }
      return getPageBlocksQuery(db, pageId);
    },
    enabled: () => Boolean(options.pageId()),
    suspense: false,
    throttleTime: options.throttleTime,
  });
}

/**
 * Backlinks: pages linking to the current one via wiki-links.
 */
export function useBacklinksQuery(
  options: BasePageQueryOptions & { pageId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<BacklinkRow>({
    queryKey: () => queryKeys.pages.backlinks(options.pageId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const pageId = options.pageId();
      if (!db || !pageId) {
        return null;
      }
      return getBacklinksQuery(db, pageId);
    },
    enabled: () => Boolean(options.pageId()),
    suspense: false,
    throttleTime: options.throttleTime,
  });
}

/**
 * Page attachments, in upload order.
 */
export function usePageAttachmentsQuery(
  options: BasePageQueryOptions & { pageId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<Attachment>({
    queryKey: () => queryKeys.pages.attachments(options.pageId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const pageId = options.pageId();
      if (!db || !pageId) {
        return null;
      }
      return getAttachmentsByPageQuery(db, pageId);
    },
    enabled: () => Boolean(options.pageId()),
    suspense: false,
    throttleTime: options.throttleTime,
  });
}

/**
 * All user blocks, in lightweight form, for analytics
 * (word count, activity by date, link map). Non-suspense:
 * statistics screen manages its own loading state.
 */
export function useAllBlocksQuery(
  options: BasePageQueryOptions & { userId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<BlockStatRow>({
    queryKey: () => queryKeys.pages.blocksAll(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      if (!db) {
        return null;
      }
      return getAllBlocksQuery(db);
    },
    enabled: () => Boolean(options.userId()),
    suspense: false,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/**
 * Page search by title.
 */
export function usePageSearchQuery(options: BasePageQueryOptions & { term: Accessor<string> }) {
  return createPowerSyncQuery<Page>({
    queryKey: () => queryKeys.pages.search(options.term()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const term = options.term().trim();
      if (!db || term.length === 0) {
        return null;
      }
      return searchPagesQuery(db, term);
    },
    enabled: () => options.term().trim().length > 0,
    suspense: false,
    throttleTime: options.throttleTime,
  });
}

/**
 * LOCAL version history of a page (PowerSync bucket bounded to
 * the sync window, newest first). Deep history is paginated
 * via API from the UI.
 */
export function usePageVersionsQuery(
  options: BasePageQueryOptions & { pageId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<PageVersion>({
    queryKey: () => queryKeys.pages.versions(options.pageId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const pageId = options.pageId();
      if (!db || !pageId) {
        return null;
      }
      return getPageVersionsQuery(db, pageId);
    },
    enabled: () => Boolean(options.pageId()),
    suspense: false,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/**
 * SPREADSHEET library: pages whose content block is a
 * workbook, most recently edited first.
 */
export function useWorkbookPagesQuery(
  options: BasePageQueryOptions & { userId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<Page>({
    queryKey: () => queryKeys.pages.workbooks(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      if (!db) {
        return null;
      }
      return getWorkbookPagesQuery(db);
    },
    enabled: () => Boolean(options.userId()),
    suspense: false,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/**
 * PRESENTATION library: pages whose content block is a deck,
 * most recently edited first.
 */
export function useDeckPagesQuery(
  options: BasePageQueryOptions & { userId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<Page>({
    queryKey: () => queryKeys.pages.decks(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      if (!db) {
        return null;
      }
      return getDeckPagesQuery(db);
    },
    enabled: () => Boolean(options.userId()),
    suspense: false,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/**
 * Trash (L2): soft-deleted pages, newest first.
 */
export function useTrashedPagesQuery(
  options: BasePageQueryOptions & { userId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<Page>({
    queryKey: () => queryKeys.pages.trash(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      if (!db) {
        return null;
      }
      return getTrashedPagesQuery(db);
    },
    enabled: () => Boolean(options.userId()),
    suspense: false,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}
